import { InviteType } from '@cc/schema';
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useNavigation,
  useParsed,
} from '@refinedev/core';
import { useEffect, useRef } from 'react';
import { LOCAL_STORAGE_KEYS } from '../../helpers/constants';
import { IUser } from '../../providers';
import {
  DeleteInvite,
  Invite,
  TherapistClientEdge,
} from '../../schema/be-graphql-generated';

const Inner = () => {
  const { params } = useParsed<{ token: string }>();
  const token = params?.token || '';

  const { mutateAsync: createEdge } = useCustomMutation<TherapistClientEdge>();
  const { mutateAsync: deleteInviteMutation } = useCustomMutation<DeleteInvite>();
  /** The redemption runs ONCE per visit, whatever re-renders the identity and invite queries cause. */
  const redeeming = useRef(false);

  const { data: identity, isLoading: identityLoading } = useGetIdentity<IUser>();
  /** A session with a `users` row — the only state in which the API will answer `inviteByToken`. */
  const signedIn = Boolean(identity?.db?._id);

  const { replace } = useNavigation();

  if (token) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.inviteToken, token);
  }

  const {
    data: inviteData,
    isLoading: inviteLoading,
    refetch: refetchInvite,
    error: inviteError,
  } = useCustom<Invite>({
    url: '',
    method: 'get',
    /**
     * NOT BEFORE A SESSION EXISTS (E03 finding 12, founder on prod 2026-09-19). This route is public
     * so an emailed link can land here signed out — and the shipped page's first act was this query,
     * which graphql-shield refuses without a session: a blank page while react-query retried, an
     * error flash, then the fallback redirect to `/` and the login page. The token is in
     * localStorage by then, which is why the flow still completed. Now the query waits for a
     * session, never retries, and a visitor without one goes to login at once.
     */
    queryOptions: { enabled: signedIn, retry: false },
    meta: {
      operation: 'inviteByToken',
      fields: [
        '_id',
        'token',
        'type',
        'from',
        'allowEdit',
        'allowView',
        'allowAdminView',
        'allowAdminEdit',
      ],
      variables: {
        token: {
          name: 'token',
          type: 'String',
          value: token,
        },
      },
    },
  });

  /**
   * THE INVITATION IS PRESENTED WITH THE GRANT (P018 catch-up, 2026-09-19 — E07 finding 42's last
   * line). `inviteToken` is an optional input field since E07 build finding 27; without it every
   * redemption on this page was recorded `grantedVia: permanentLink` with no `inviteId`, and the
   * invitation was removed by a second call. Now the token travels with the edge, so the E lane
   * records `grantedVia: invite` + the inviteId and SPENDS the invitation in the same append; the
   * legacy lane still leaves the row, so it is removed here only if it is still there.
   *
   * IN SEQUENCE, NOT IN A RACE. refine's `mutate` is fire-and-forget, and the shipped page fired the
   * edge and the delete back to back — with the token presented, the delete could reach the
   * invitation first and the grant then be refused for an invitation that no longer existed (one
   * red run in two, 2026-09-19). `mutateAsync` makes the order real, and the page leaves only
   * after the redemption has finished.
   */
  const redeemInvitation = async () => {
    if (!identity?.db?.practiceId || !token) return;
    try {
      await createEdge({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'createTherapistClientEdge',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'therapistClientECreateInput',
              value: {
                data: {
                  from: identity?.db?._id,
                  to: inviteData?.data.from,
                  practiceId: identity?.db?.practiceId,
                  allowEdit: inviteData?.data.allowEdit,
                  allowView: inviteData?.data.allowView,
                  allowAdminView: inviteData?.data.allowAdminView,
                  allowAdminEdit: inviteData?.data.allowAdminEdit,
                  inviteToken: token,
                },
              },
            },
          },
        },
      });
      const still = await refetchInvite();
      if (still.data?.data?._id) {
        await deleteInviteMutation({
          url: '',
          method: 'post',
          values: {},
          meta: {
            operation: 'deleteInvite',
            fields: ['_id'],
            variables: {
              input: {
                name: 'input',
                type: 'deleteInviteInput',
                value: {
                  where: {
                    id: still.data.data._id,
                  },
                },
              },
            },
          },
        });
      }
    } finally {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.inviteToken);
      replace('/');
    }
  };

  useEffect(() => {
    if (identityLoading || (identity && !identity.db)) {
      return; // who is here decides everything below — wait for the answer
    }
    if (!signedIn) {
      replace('/login'); // the token is saved above; login → the wizard pre-fills it
      return;
    }
    if (inviteLoading) {
      return; // the answer decides the route — do not leave before it arrives
    }
    const redeemable =
      Boolean(token) &&
      Boolean(identity?.db?.practiceId) &&
      Boolean(inviteData?.data?._id) &&
      inviteData?.data?.type === InviteType.THERAPIST;
    if (redeemable && !redeeming.current) {
      redeeming.current = true;
      redeemInvitation();
      return;
    }
    if (redeeming.current) {
      return; // the redemption redirects when it is done
    }
    const handle = setTimeout(() => {
      replace('/');
    }, 800);
    return () => clearTimeout(handle);
  }, [identity, identityLoading, signedIn, token, inviteData, inviteLoading]);

  return null;
};

export const InviteJoin = () => {
  return <Inner />;
};
